import { withRetry } from "./aiUtilities";

// ─── PROXY IDENTITY HEADERS (SEC-1) ─────────────────────────────────────────
// Server (loopback pipeline calls): per-boot internal token set by server.ts.
// Browser: Firebase ID token (SDK caches; not a per-call network hit).
// Fails open ({}): while PROXY_ENFORCE is off nothing is blocked, and after
// enforcement a missing token correctly reads as anonymous.
export async function getProxyAuthHeaders(): Promise<Record<string, string>> {
  if (typeof window === 'undefined') {
    const t = process.env.INTERNAL_PROXY_TOKEN;
    return t ? { 'x-oracle-internal': t } : {};
  }
  try {
    const { auth } = await import('../firebase');
    const token = await auth.currentUser?.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function fetchFMP(endpoint: string, params: Record<string, string> = {}) {
  let finalEndpoint = endpoint;
  
  // Normalize params keys to lowercase to avoid "Symbol" vs "symbol" issues
  const normalizedParams: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    // Canonicalize only the symbol key (handles "Symbol" vs "symbol");
    // preserve case for all other keys so FMP's case-sensitive camelCase
    // screener params (marketCapMoreThan, volumeMoreThan, isEtf, etc.) survive.
    normalizedParams[k.toLowerCase() === 'symbol' ? 'symbol' : k] = v;
  }

  let symbol = normalizedParams.symbol;
  const { symbol: _, ...otherParams } = normalizedParams;

  // FMP Ticker Normalization
  if (symbol) {
    symbol = symbol.toUpperCase().replace(/\./g, '-').replace(/\//g, '-').replace(/\s+/g, '-');
  }
  
  // Berkshire B special case
  const isBerkshireB = symbol === 'BRK-B' || symbol === 'BRK.B';
  
  // Strip symbol from endpoint if it was appended to path by AI
  if (symbol && finalEndpoint.endsWith(`/${symbol}`)) {
    finalEndpoint = finalEndpoint.substring(0, finalEndpoint.length - symbol.length - 1);
  } else if (symbol && finalEndpoint.endsWith(`/${symbol.replace(/-/g, '.')}`)) {
    finalEndpoint = finalEndpoint.substring(0, finalEndpoint.lastIndexOf('/'));
  }

  // FMP Stable Pattern: Use query parameters for symbols consistently.
  let baseUrl = `/api/fmp/${finalEndpoint}`;
  const searchParams = new URLSearchParams();
  
  if (symbol) {
    searchParams.append('symbol', symbol);
  }

  for (const [key, value] of Object.entries(otherParams)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  }
  
  const queryString = searchParams.toString();
  const connector = baseUrl.includes('?') ? '&' : '?';
  const finalUrl = queryString ? `${baseUrl}${connector}${queryString}` : baseUrl;
  
  console.log(`[FMP Client] Fetching: ${finalUrl} (Params: ${JSON.stringify(normalizedParams)})`);
  
  return withRetry(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
    
    try {
      let absoluteUrl = finalUrl;
      if (typeof window === 'undefined' && finalUrl.startsWith('/')) {
        const port = 3000;
        absoluteUrl = `http://127.0.0.1:${port}${finalUrl}`;
      }
      const response = await fetch(absoluteUrl, { signal: controller.signal, headers: await getProxyAuthHeaders() });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        const isJson = contentType && contentType.includes("application/json");
        const errorData = isJson ? await response.json().catch(() => ({})) : {};
        const errorMsg = errorData.error || response.statusText;
        const code = errorData.code;
        
        if (response.status === 403 || response.status === 402 || 
           (response.status === 429 && code === "RATE_LIMIT_REACHED") ||
           (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes("limit reach"))) {
          console.warn(`FMP API Plan/Limit Restriction (${response.status}) for ${endpoint}: ${errorMsg}. Falling back to AI search.`);
          return { _restricted: true, endpoint };
        }
        
        if (response.status === 404 && isBerkshireB) {
          // Try fallback with dot if dash failed for Berkshire
          const fallbackSymbol = symbol.replace('-', '.');
          console.log(`[FMP Client] Ticker fallback for ${endpoint}: Trying ${fallbackSymbol}`);
          // Recursive call with dot (prevent infinite loop by checking if we already tried dot)
          if (symbol.includes('-')) {
             return fetchFMP(endpoint, { ...params, symbol: fallbackSymbol });
          }
        }

        if (response.status === 429 || response.status >= 500) {
          throw { status: response.status, message: `FMP API Error: ${errorMsg}` };
        }
        return null;
      }
      const contentType = response.headers.get("content-type");
      if (contentType && !contentType.includes("application/json")) {
        const text = await response.text();
        console.error(`[FMP Client] Unexpected content-type (${contentType}) for ${finalUrl}: ${text.substring(0, 200)}`);
        throw new Error(`Unexpected content-type: ${contentType}`);
      }
      return await response.json();
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('FMP API Timeout');
      }
      throw error;
    }
  }, 3);
}
export async function fetchUsdConversionRate(reportedCurrency: string): Promise<number> {
  if (!reportedCurrency || reportedCurrency.toUpperCase() === 'USD') return 1;
  const pair = `${reportedCurrency.toUpperCase()}USD`;
  try {
    const data = await fetchFMP(`stable/quote`, { symbol: pair });
    const rate = Array.isArray(data) ? data[0]?.price : data?.price;
    if (typeof rate === 'number' && rate > 0) {
      const localPerUsd = 1 / rate;
      console.log(`[CURRENCY] ${pair}: 1 ${reportedCurrency} = ${rate} USD (localPerUsd=${localPerUsd.toFixed(4)})`);
      return localPerUsd;
    }
    console.error(`[CURRENCY-FAIL] Could not parse rate for ${pair}. Raw:`, JSON.stringify(data).slice(0, 200));
    return 1;
  } catch (err) {
    console.error(`[CURRENCY-FAIL] forex fetch failed for ${pair}:`, err);
    return 1;
  }
}
let polygonLastRequestTime = 0;
const POLYGON_MIN_DELAY = 12000;
export async function fetchPolygon(endpoint: string, params: Record<string, string> = {}) {
  let baseUrlStr = `/api/polygon/${endpoint}`;
  if (typeof window === 'undefined') {
    const port = 3000;
    baseUrlStr = `http://127.0.0.1:${port}${baseUrlStr}`;
  } else {
    baseUrlStr = window.location.origin + baseUrlStr;
  }
  const url = new URL(baseUrlStr);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.append(key, value);
  }
  
  return withRetry(async () => {
    // Basic rate limiting: ensure at least POLYGON_MIN_DELAY between requests
    const now = Date.now();
    const timeSinceLast = now - polygonLastRequestTime;
    if (timeSinceLast < POLYGON_MIN_DELAY) {
      const waitTime = POLYGON_MIN_DELAY - timeSinceLast;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    polygonLastRequestTime = Date.now();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
    
    try {
      const response = await fetch(url.toString(), { signal: controller.signal, headers: await getProxyAuthHeaders() });
      clearTimeout(timeoutId);
      if (!response.ok) {
        if (response.status === 429) {
          throw { status: 429, message: `Polygon API Rate Limit for ${endpoint}` };
        }
        // Silence 404s for Polygon as it's often used as a fallback or for peers that might not exist there
        if (response.status !== 404) {
          console.warn(`Polygon API Error (${response.status}) for ${endpoint}.`);
        }
        return null;
      }
      return await response.json();
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Polygon API Timeout');
      }
      throw error;
    }
  }, 5); // Max 5 retries for Polygon
}
let alphaVantageLastRequestTime = 0;
const ALPHAVANTAGE_MIN_DELAY = 12000;
export async function fetchAlphaVantage(params: Record<string, string> = {}) {
  let baseUrlStr = `/api/alphavantage`;
  if (typeof window === 'undefined') {
    const port = 3000;
    baseUrlStr = `http://127.0.0.1:${port}${baseUrlStr}`;
  } else {
    baseUrlStr = window.location.origin + baseUrlStr;
  }
  const url = new URL(baseUrlStr);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.append(key, value);
  }
  
  return withRetry(async () => {
    // Basic rate limiting: ensure at least ALPHAVANTAGE_MIN_DELAY between requests
    const now = Date.now();
    const timeSinceLast = now - alphaVantageLastRequestTime;
    if (timeSinceLast < ALPHAVANTAGE_MIN_DELAY) {
      const waitTime = ALPHAVANTAGE_MIN_DELAY - timeSinceLast;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    alphaVantageLastRequestTime = Date.now();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
    
    try {
      const response = await fetch(url.toString(), { signal: controller.signal, headers: await getProxyAuthHeaders() });
      clearTimeout(timeoutId);
      if (!response.ok) {
        if (response.status === 429) {
          throw { status: 429, message: "Alpha Vantage Rate Limit" };
        }
        return null;
      }
      return await response.json();
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Alpha Vantage API Timeout');
      }
      throw error;
    }
  }, 3);
}
export async function fetchAvOverview(symbol: string): Promise<{ IPO?: string } | null> {
  try {
    let baseUrlStr = `/api/alphavantage`;
    if (typeof window === 'undefined') {
      baseUrlStr = `http://127.0.0.1:3000${baseUrlStr}`;
    } else {
      baseUrlStr = window.location.origin + baseUrlStr;
    }
    const url = new URL(baseUrlStr);
    url.searchParams.append("function", "OVERVIEW");
    url.searchParams.append("symbol", symbol);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s hard cap

    const response = await fetch(url.toString(), { signal: controller.signal, headers: await getProxyAuthHeaders() });
    clearTimeout(timeoutId);

    if (!response.ok) return null;

    const data = await response.json();

    // AV returns {} or { "Information": "..." } when rate-limited or key absent
    if (!data || !data.Symbol || data.Information || data["Error Message"]) return null;

    return data as { IPO?: string };
  } catch {
    // AbortError (timeout), network error, JSON parse error — all treated as null
    return null;
  }
}
